import React, { useEffect, useRef, useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import TaskCard from "./TaskCard";
import { defaultMoveTargetId, isSystemColumn } from "./columnUtils";

function SortableTask({ task, onTaskClick, dragDisabled, filterHighlight, subtasks, onToggleSubtask }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `task-${task.id}`,
    data: { type: "task", task },
    disabled: dragDisabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const dragProps = dragDisabled ? null : { ...attributes, ...listeners };

  return (
    <div ref={setNodeRef} style={style} className="kanban-task-wrapper">
      <TaskCard
        task={task}
        onClick={() => onTaskClick(task)}
        dragHandleProps={dragProps}
        isDragging={isDragging}
        filterHighlight={filterHighlight}
        subtasks={subtasks}
        onToggleSubtask={onToggleSubtask}
      />
    </div>
  );
}

function GripIcon() {
  return (
    <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor" aria-hidden="true">
      <circle cx="3" cy="3" r="1.4" />
      <circle cx="9" cy="3" r="1.4" />
      <circle cx="3" cy="8" r="1.4" />
      <circle cx="9" cy="8" r="1.4" />
      <circle cx="3" cy="13" r="1.4" />
      <circle cx="9" cy="13" r="1.4" />
    </svg>
  );
}

function DeleteColumnDialog({ column, columns, taskCount, onCancel, onConfirm }) {
  const [moveTo, setMoveTo] = useState(() => defaultMoveTargetId(columns, column.id));
  const [saving, setSaving] = useState(false);
  const titleId = `delete-column-title-${column.id}`;
  const needsMove = taskCount > 0;
  const targets = columns.filter((col) => col.id !== column.id);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && !saving) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel, saving]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (needsMove && !moveTo) return;
    setSaving(true);
    try {
      await onConfirm(needsMove ? Number(moveTo) : null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="task-modal-overlay kanban-column-dialog-overlay" role="presentation" onClick={onCancel}>
      <div
        className="data-card kanban-column-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId}>Удалить колонку</h2>
        {needsMove ? (
          <p>
            В «{column.name}» задач: {taskCount}. Куда перенести, затем удалить колонку?
          </p>
        ) : (
          <p>Удалить пустую колонку «{column.name}»?</p>
        )}
        <form onSubmit={handleSubmit}>
          {needsMove && (
            <label className="task-form-label">
              Перенести задачи в
              <select
                className="task-form-select"
                name="move_to"
                value={moveTo || ""}
                onChange={(e) => setMoveTo(e.target.value)}
                autoComplete="off"
              >
                {targets.map((col) => (
                  <option key={col.id} value={col.id}>
                    {col.name}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="kanban-column-dialog-actions">
            <button type="button" className="btn btn-sm" onClick={onCancel} disabled={saving}>
              Отмена
            </button>
            <button
              type="submit"
              className="btn btn-sm task-btn-danger"
              disabled={saving || (needsMove && !moveTo)}
            >
              {saving ? "Удаление…" : "Удалить колонку"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function KanbanColumn({
  column,
  columns,
  tasks,
  totalTaskCount,
  onTaskClick,
  dragDisabled,
  matchingTaskIds,
  onRenameColumn,
  onDeleteColumn,
  onMoveColumn,
  onToggleCollapsed,
  collapsed = false,
  columnIndex,
  columnCount,
  subtasksByParent,
  onToggleSubtask,
}) {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const menuRef = useRef(null);

  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `column-${column.id}`,
    data: { type: "column", column },
  });

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `column-drop-${column.id}`,
    data: { type: "column", column },
    disabled: dragDisabled,
  });

  const setColumnRef = (node) => {
    setSortableRef(node);
    if (collapsed) setDroppableRef(node);
  };

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("click", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const taskIds = tasks.map((t) => `task-${t.id}`);
  const countLabel =
    totalTaskCount != null && totalTaskCount !== tasks.length
      ? `${tasks.length} / ${totalTaskCount}`
      : String(tasks.length);
  const slugClass = column.slug ? `kanban-column-${column.slug.replace("_", "-")}` : "";
  const system = isSystemColumn(column);
  const canMoveLeft = columnIndex > 0;
  const canMoveRight = columnIndex < columnCount - 1;

  const handleStartRename = () => {
    setMenuOpen(false);
    setEditName(column.name);
    setEditing(true);
  };

  const handleRename = async (e) => {
    e.preventDefault();
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await onRenameColumn(column.id, { name: editName.trim() });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleMove = (delta) => {
    setMenuOpen(false);
    const next = columnIndex + delta;
    if (next < 0 || next >= columnCount) return;
    onMoveColumn?.(column.id, next);
  };

  const handleAskDelete = () => {
    setMenuOpen(false);
    if (system) return;
    setConfirmDelete(true);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <section
      ref={setColumnRef}
      style={style}
      className={`kanban-column ${slugClass} ${isOver ? "kanban-column-over" : ""} ${
        dragDisabled ? "kanban-column-drag-disabled" : ""
      } ${isDragging ? "kanban-column-dragging" : ""} ${
        collapsed ? "kanban-column-collapsed" : ""
      }`}
    >
      <header className="kanban-column-header">
        <div className="kanban-column-title">
          <button
            type="button"
            className="kanban-column-grip"
            ref={setActivatorNodeRef}
            aria-label={`Переместить колонку ${column.name}`}
            title="Переместить колонку"
            {...attributes}
            {...listeners}
          >
            <GripIcon />
          </button>
          {editing && !collapsed ? (
            <form className="kanban-column-rename-form" onSubmit={handleRename}>
              <input
                className="kanban-column-rename-input"
                name="column_name"
                autoComplete="off"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                maxLength={100}
                autoFocus
                aria-label="Название колонки"
                onBlur={() => {
                  if (!saving) setEditing(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setEditing(false);
                }}
              />
            </form>
          ) : (
            <h3>
              <button
                type="button"
                className="kanban-column-name-editable"
                onClick={collapsed ? () => onToggleCollapsed?.(column.id) : handleStartRename}
                title={
                  collapsed
                    ? `Развернуть колонку ${column.name}`
                    : `${column.name} — нажмите, чтобы переименовать`
                }
              >
                {column.name}
              </button>
            </h3>
          )}
        </div>
        <div className="kanban-column-actions">
          <span className="kanban-column-count">{countLabel}</span>
          <button
            type="button"
            className="kanban-column-collapse-btn"
            aria-pressed={collapsed}
            aria-label={collapsed ? `Развернуть колонку ${column.name}` : `Свернуть колонку ${column.name}`}
            title={collapsed ? "Развернуть" : "Свернуть"}
            onClick={() => onToggleCollapsed?.(column.id)}
          >
            {collapsed ? "›" : "‹"}
          </button>
          <div className="kanban-column-more" ref={menuRef}>
            <button
              type="button"
              className="kanban-column-menu-btn"
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              aria-label={`Действия колонки ${column.name}`}
              title="Действия колонки"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((v) => !v);
              }}
            >
              ⋯
            </button>
            {menuOpen && (
              <div className="kanban-column-menu" role="menu">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onToggleCollapsed?.(column.id);
                  }}
                >
                  {collapsed ? "Развернуть" : "Свернуть"}
                </button>
                <button type="button" role="menuitem" onClick={handleStartRename} disabled={collapsed}>
                  Переименовать
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleMove(-1)}
                  disabled={!canMoveLeft}
                >
                  Влево
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => handleMove(1)}
                  disabled={!canMoveRight}
                >
                  Вправо
                </button>
                {!system && (
                  <button
                    type="button"
                    role="menuitem"
                    className="kanban-column-menu-danger"
                    onClick={handleAskDelete}
                  >
                    Удалить колонку
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {!collapsed && (
        <div ref={setDroppableRef} className="kanban-column-body">
          <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
            {tasks.length > 0 ? (
              tasks.map((task) => (
                <SortableTask
                  key={task.id}
                  task={task}
                  onTaskClick={onTaskClick}
                  dragDisabled={dragDisabled}
                  filterHighlight={matchingTaskIds?.has(task.id)}
                  subtasks={subtasksByParent?.[task.id] || []}
                  onToggleSubtask={onToggleSubtask}
                />
              ))
            ) : (
              <div className="kanban-column-empty">
                <span>{dragDisabled ? "Нет задач по фильтру" : "Перетащите задачу сюда"}</span>
              </div>
            )}
          </SortableContext>
        </div>
      )}

      {confirmDelete && (
        <DeleteColumnDialog
          column={column}
          columns={columns}
          taskCount={totalTaskCount != null ? totalTaskCount : tasks.length}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={async (moveTo) => {
            await onDeleteColumn(column.id, moveTo);
            setConfirmDelete(false);
          }}
        />
      )}
    </section>
  );
}
